import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ButtonProps, IconButton, MenuItem, styled } from '@mui/material';
import {
    DataGridPremium,
    GridColDef,
    GridCsvExportOptions,
    GridCsvGetRowsToExportParams,
    GridExportMenuItemProps,
    GridRowId,
    GridRowSelectionModel,
    GridToolbarContainer,
    GridToolbarContainerProps,
    GridToolbarExportContainer,
    GridToolbarFilterButton,
    gridExpandedSortedRowIdsSelector,
    useGridApiContext,
} from '@mui/x-data-grid-premium';
import Icon from '@mdi/react';
import { mdiLinkVariant } from '@mdi/js';

import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { updateSelectedRecommendations } from '../../state/features/networkRxSlice';
import { Recommendation } from '../../interfaces/services/networkRecommendations';
import { ApiStatus } from '../../utils/enums';
import { buildLaneLink } from '../../utils/navigationHelpers';
import { formatHealthFilters } from '../../utils/stateHelpers';
import { useFilters } from '../Filters/useFilters';

const classesPrefix = 'stopServiceTable';

const classes = {
    table: `${classesPrefix}-table`,
};

const StyledDataGridPremium = styled(DataGridPremium)(({ theme }) => {
    return {
        [`&.${classes.table}`]: {
            '&.MuiDataGrid-root .MuiDataGrid-cell:focus': {
                outline: 0,
            },
            '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
                outline: 0,
            },
            '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within': {
                outline: 0,
            },
            '&.MuiDataGrid-root .MuiDataGrid-toolbarContainer': {
                justifyContent: 'right',
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
            },
        },
    };
}) as typeof DataGridPremium;

const getFilteredRows = ({ apiRef }: GridCsvGetRowsToExportParams): GridRowId[] => {
    return gridExpandedSortedRowIdsSelector(apiRef);
};

const ExportCsvMenuItem = ({
    options,
    hideMenu,
}: GridExportMenuItemProps<{
    label: string;
    csvOptions: GridCsvExportOptions;
}>): React.JSX.Element => {
    const apiRef = useGridApiContext();

    const handleExport = (): void => {
        apiRef.current.exportDataAsCsv({
            ...options?.csvOptions,
            fileName: 'Network Insights - Stop Service Recommendations',
        });
    };

    return (
        <MenuItem
            onClick={(): void => {
                handleExport();

                // Hide the export menu after the export
                hideMenu?.();
            }}
        >
            {options?.label}
        </MenuItem>
    );
};

const CustomExportButton = (props: ButtonProps): React.JSX.Element => {
    return (
        <GridToolbarExportContainer {...props}>
            <ExportCsvMenuItem options={{ label: 'CSV', csvOptions: { allColumns: true, getRowsToExport: getFilteredRows } }} />
        </GridToolbarExportContainer>
    );
};

const CustomToolbar = (props: GridToolbarContainerProps): React.JSX.Element => {
    return (
        <GridToolbarContainer {...props}>
            <GridToolbarFilterButton />
            <CustomExportButton />
        </GridToolbarContainer>
    );
};

const StopServiceTable = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const filters = useFilters();

    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

    const networkRecommendationsApiStatus = useAppSelector((state) => { return state.networkRx.networkRecommendationsApiStatus; });
    const networkRecommendations = useAppSelector((state) => { return state.networkRx.networkRecommendations; });

    const healthFilters = formatHealthFilters();

    const columns = useMemo((): GridColDef<Recommendation>[] => {
        return [
            {
                headerName: 'Shipper',
                field: 'target_pickup_party_name',
                flex: 1,
            },
            {
                headerName: 'Origin',
                field: 'pickup_service_area_name',
                flex: 1,
            },
            {
                headerName: 'Destination',
                field: 'delivery_service_area_name',
                flex: 1,
            },
            {
                headerName: 'Learn More',
                field: 'lane',
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                align: 'center',
                valueGetter: (_value, row): string => {
                    return `${window.location.protocol}//${window.location.hostname}${buildLaneLink(row.pickup_service_area_id, row.delivery_service_area_id, {
                        ...filters,
                        earliest_delivery_departure_timestamp: healthFilters.earliest_delivery_departure_timestamp,
                        latest_delivery_departure_timestamp: healthFilters.latest_delivery_departure_timestamp,
                    })}`;
                },
                renderCell: ({ row }): React.JSX.Element => {
                    return (
                        <IconButton
                            color='primary'
                            component={RouterLink}
                            to={buildLaneLink(row.pickup_service_area_id, row.delivery_service_area_id, {
                                ...filters,
                                earliest_delivery_departure_timestamp: healthFilters.earliest_delivery_departure_timestamp,
                                latest_delivery_departure_timestamp: healthFilters.latest_delivery_departure_timestamp,
                            })}
                        >
                            <Icon path={mdiLinkVariant} size={1} />
                        </IconButton>
                    );
                },
            },
        ];
    }, [filters, healthFilters.earliest_delivery_departure_timestamp, healthFilters.latest_delivery_departure_timestamp]);

    const handleRowSelectionChange = (newRowSelectionModel: GridRowSelectionModel): void => {
        // if the existing selected id matches the new one, clear out the selection, otherwise use what MUI passes
        const newModel = newRowSelectionModel.length > 0 && rowSelectionModel.length > 0 &&
            newRowSelectionModel[0] === rowSelectionModel[0] ?
            [] :
            newRowSelectionModel;
        setRowSelectionModel(newModel);

        // if a row is selected, find the full lane details and save to the store, otherwise clear the selected recommendation
        if (newModel.length > 0) {
            const [recommendationId, laneIndex] = newModel[0].toString().split('_');
            const selectedRow = networkRecommendations.recommendations.stop_service.find((recommendation) => {
                return recommendation.recommendation_id === recommendationId &&
                    recommendation.lane_index.toString() === laneIndex;
            });

            dispatch(updateSelectedRecommendations(selectedRow ? [selectedRow] : []));
        } else {
            dispatch(updateSelectedRecommendations([]));
        }
    };

    const columnSearchPredicate = (column: GridColDef, searchValue: string): boolean => {
        return (
            (column.headerName || column.field).toLowerCase().indexOf(searchValue) > -1 ||
            (column.description || '').toLowerCase().indexOf(searchValue) > -1
        );
    };

    return (
        <StyledDataGridPremium
            classes={{
                root: classes.table,
            }}
            rows={networkRecommendations.recommendations.stop_service}
            columns={columns}
            density='compact'
            disableMultipleRowSelection
            disableRowGrouping
            disableAggregation
            loading={networkRecommendationsApiStatus === ApiStatus.Idle || networkRecommendationsApiStatus === ApiStatus.Loading}
            getRowId={(row): string => { return `${row.recommendation_id}_${row.lane_index}`; }}
            // rowCount={networkRecommendations.recommendation_count_details.stop_service}
            pagination
            pageSizeOptions={[5, 10, 25, 50, 100]}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={handleRowSelectionChange}
            initialState={{
                sorting: {
                    sortModel: [
                        { field: 'pickup_service_area_name', sort: 'asc' },
                        { field: 'delivery_service_area_name', sort: 'asc' },
                        { field: 'target_pickup_party_name', sort: 'asc' },
                    ],
                },
            }}
            slots={{
                toolbar: CustomToolbar,
            }}
            slotProps={{
                columnsManagement: {
                    searchPredicate: columnSearchPredicate,
                },
            }}
        />
    );
};

export default StopServiceTable;
